import style from "../../assets/scss/common/header.module.scss";
import downloadIcon from "../../assets/images/header/download_icon.svg";
import loginIcon from "../../assets/images/header/login_icon.svg";
import expandedIcon from "../../assets/images/header/expanded_icon.svg";
import collapsedIcon from "../../assets/images/header/collapsed_icon.svg";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector, useAuth, useModal, useUserAgent } from "../../hooks/hooks";
import { VoucherCount } from "../../types/voucher";
import { getOrganVoucherCountAsync, getUserVoucherCountAsync } from "../../store/voucher/voucherSlice";
import { CouponCount } from "../../types/coupon";
import { getUserCouponCountAsync, setUserCouponCount, userCouponCountState } from "../../store/coupon/couponSlice";
import { ModalType } from "../../types/common";
import { CouponVoucherStatus } from "./CouponVoucherStatus";
import { useClickEvent } from "../../hooks/useClickEvent";
import { useUserInfoGetter } from "./hooks/useUserInfoGetter";
import { useLocation } from "react-router-dom";
import { getProfileAuthTokenAsync, getProfilesAsync } from "../../store/profile/profileSlice";

interface Props {
  signOut: VoidFunction;
}

export function LoginBar({ signOut }: Props) {
  const dispatch = useAppDispatch();

  const [userVoucherCount, setUserVoucherCount] = useState(0);
  const [organVoucherCount, setOrganVoucherCount] = useState(0);
  const [organKpassVoucherCount, setOrganKpassVoucherCount] = useState(0);
  const userCouponCount = useAppSelector(userCouponCountState);
  const [isCouponVoucherCollapsed, setIsCouponVoucherCollapsed] = useState(true);

  const { isLoggedIn } = useAuth();
  const { openModal } = useModal();
  const { isDesktop } = useUserAgent();
  const { getUserInfo, user } = useUserInfoGetter();

  // 임시처리
  const location = useLocation().pathname;

  const onClickAppDownload = (e: React.MouseEvent) => {
    openModal(ModalType.APP_DOWNLOAD);
    e.stopPropagation();
  };
  const onClickLoginBtn = (e: React.MouseEvent) => {
    openModal(ModalType.LOGIN);
    e.stopPropagation();
  };

  //  매번 인증없이 프로세스 위해 추가했었음
  // const onClickTest = (e: React.MouseEvent) => {
  //   openModal(ModalType.SIGN_UP);
  //   e.stopPropagation();
  // };

  const onClickLogoutBtn = () => signOut();

  const getUserVoucherCount = useCallback(async () => {
    try {
      const { count }: VoucherCount = await dispatch(getUserVoucherCountAsync()).unwrap();
      setUserVoucherCount(count);
    } catch (error) {
      console.error(error);
    }
  }, [dispatch]);

  const getOrganVoucherCount = useCallback(async () => {
    try {
      const { count }: VoucherCount = await dispatch(getOrganVoucherCountAsync()).unwrap();
      setOrganVoucherCount(count);
    } catch (error) {
      console.error(error);
    }
  }, [dispatch]);

  const getCouponCount = useCallback(async () => {
    try {
      const { count }: CouponCount = await dispatch(getUserCouponCountAsync()).unwrap();
      dispatch(setUserCouponCount(count));
    } catch (error) {
      console.error(error);
    }
  }, [dispatch]);

  const getProfileList = useCallback(async () => {
    try {
      // 모든 프로필 정보를 가져옴
      const response = await dispatch(getProfilesAsync()).unwrap();

      let totalKpassQuantity = 0;

      // 모든 프로필을 순회하면서 accessToken을 가져오고, voucher 개수를 계산
      const promises = response.map(async (profile: any) => {
        const myProfileIndex = profile.idx;

        // 각 프로필에 대해 accessToken을 가져옴
        const profileResponse = await dispatch(getProfileAuthTokenAsync({ userProfileIdx: myProfileIndex })).unwrap();

        const accessToken = profileResponse.accessToken;

        // accessToken을 이용해 voucher 정보를 가져옴
        const response2 = await fetch(`${process.env.REACT_APP_BASE_URL}api/v1/dcas/game-content/profile/voucher`, {
          method: "GET",
          headers: {
            Accept: "*/*",
            Authorization: `Bearer ${accessToken}`,
          },
        });

        const data = await response2.json();

        // "organ_manager"와 "kpass"에 해당하는 voucher 정보를 필터링
        const organKpass = data.voucherInformation.find(
          (voucher: any) => voucher.whose === "ORGAN_MANAGER" && voucher.unitVoucherType === "KPASS"
        );

        if (organKpass) {
          totalKpassQuantity += organKpass.quantity;
        }
      });

      // 모든 프로필에 대한 비동기 작업이 완료될 때까지 기다림
      await Promise.all(promises);

      // console.log("총 KPASS 이용권 개수:", totalKpassQuantity);
      setOrganKpassVoucherCount(totalKpassQuantity);
    } catch (error) {
      console.log(error);
    }
  }, [dispatch]);

  useEffect(() => {
    if (isLoggedIn) {
      if (!user) {
        getUserInfo().then();
      }
      getUserVoucherCount().then();
      getOrganVoucherCount().then();
      getProfileList().then();
      getCouponCount().then();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, user]);

  const LoginBtn = () => (
    <>
      {location !== "/withuser" && (
        <button className={style.loginBtn} onClick={onClickLoginBtn}>
          <img className={style.icon} src={loginIcon} alt="login-icon" />
          <span className={style.text}>로그인 & 회원가입</span>
        </button>
      )}
      {/*<button className={style.loginBtn} onClick={onClickTest}>*/}
      {/*  <img className={style.icon} src={loginIcon} alt="login-icon"/>*/}
      {/*  <span className={style.text}>가입 테스트</span>*/}
      {/*</button>*/}
    </>
  );

  const couponVoucherOptionsRef = useRef(null);
  useClickEvent({ elementRefs: [couponVoucherOptionsRef], doOnClickBg: () => setIsCouponVoucherCollapsed(true) });

  const LoggedInContent = () => (
    <div className={style.loggedInStatus}>
      {isDesktop ? (
        <CouponVoucherStatus
          couponCount={userCouponCount}
          organVoucherCount={organVoucherCount}
          organKpassVoucherCount={organKpassVoucherCount}
          userVoucherCount={userVoucherCount}
        />
      ) : (
        <></>
      )}
      {isDesktop ? (
        <>
          <span className={style.userName}>
            <span className={style.accent}>{user?.name}</span> 님
          </span>
        </>
      ) : (
        <div className={style.myInfoWrapper}>
          <span className={style.userName}>
            <span className={style.accent}>{user?.name}</span> 님
          </span>
          <div className={style.voucherCouponButtonWrapper}>
            <button
              className={style.voucherCouponButton}
              onClick={(e) => {
                e.stopPropagation();
                setIsCouponVoucherCollapsed(!isCouponVoucherCollapsed);
              }}
            >
              <span>쿠폰·이용권</span>
              <img src={isCouponVoucherCollapsed ? collapsedIcon : expandedIcon} alt={"펼침 아이콘"} />
            </button>
            {isCouponVoucherCollapsed ? (
              <></>
            ) : (
              <div ref={couponVoucherOptionsRef}>
                <CouponVoucherStatus
                  couponCount={userCouponCount}
                  organVoucherCount={organVoucherCount}
                  organKpassVoucherCount={organKpassVoucherCount}
                  userVoucherCount={userVoucherCount}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <button className={style.logoutBtn} onClick={onClickLogoutBtn}>
        로그아웃
      </button>
    </div>
  );

  return (
    <>
      <div className={style.loginBar}>
        <div className={style.innerBar}>
          {!isLoggedIn ? (
            <div className={style.downloadNotice}>
              {/* {location !== "/withuser" && (
                <>
                  <span className={style.text}>
                    {isDesktop ? (
                      <>
                        <span className={style.accent}>K-PASS with 앱</span> 에서 회원가입 해주세요!
                      </>
                    ) : (
                      <>
                        <span className={style.accent}>K-PASS with 앱</span> 회원가입
                      </>
                    )}
                  </span>
                  <span className={style.downloadText} onClick={onClickAppDownload}>
                    앱 다운로드
                    <img src={downloadIcon} alt="download-icon" />
                  </span>
                </>
              )} */}
            </div>
          ) : (
            <div></div>
          )}
          {isLoggedIn ? <LoggedInContent /> : <LoginBtn />}
        </div>
      </div>
    </>
  );
}
